import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { Toaster, toast } from "sonner";
import { ApiError } from "./api/client";
import { me } from "./api/auth";
import { EventStreamProvider } from "./hooks/useEventStream";
import { Layout } from "./components/layout";
import { LoginPage } from "./routes/login";
import { RunsListPage } from "./routes/runs.index";
import { RunDetailPage } from "./routes/runs.detail";
import { DomainsListPage } from "./routes/domains.index";
import { DomainDetailPage } from "./routes/domains.detail";
import { ChecksListPage } from "./routes/checks.index";
import { CheckEditPage } from "./routes/checks.detail";
import { ActionsListPage } from "./routes/actions.index";
import { ActionEditPage } from "./routes/actions.detail";
import { CohortsPage } from "./routes/cohorts.index";
import { CohortDetailPage } from "./routes/cohorts.detail";
import { ExtractionListPage } from "./routes/extraction.index";
import { ExtractionEditPage } from "./routes/extraction.detail";
import { IgnorePage } from "./routes/ignore";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false },
  },
  // Global error handler — wires onError on the cache itself (the post-construction
  // mutation we used before is a no-op in TanStack Query v5).
  queryCache: new QueryCache({
    onError: (err) => {
      if (err instanceof ApiError && err.status === 401) {
        const next = window.location.pathname + window.location.search;
        if (window.location.pathname !== "/login") {
          window.location.replace(`/login?next=${encodeURIComponent(next)}`);
        }
        return;
      }
      toast.error(err instanceof Error ? err.message : "Request failed");
    },
  }),
});

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ location }) => {
    try { await me(); }
    catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        throw redirect({
          to: "/login",
          search: { next: location.pathname + location.searchStr },
        });
      }
      throw e;
    }
  },
  component: () => (
    <EventStreamProvider>
      <Layout><Outlet /></Layout>
    </EventStreamProvider>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  beforeLoad: () => { throw redirect({ to: "/runs" }); },
  component: () => null,
});

const runsListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/runs",
  component: RunsListPage,
});

const runDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/runs/$runId",
  component: RunDetailPage,
});

const domainsListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/domains",
  component: DomainsListPage,
});

const domainDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/domains/$domainId",
  component: DomainDetailPage,
});

const checksListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/checks",
  component: ChecksListPage,
});

const checkEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/checks/$checkId",
  component: CheckEditPage,
});

const actionsListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/actions",
  component: ActionsListPage,
});

const actionEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/actions/$actionId",
  component: ActionEditPage,
});

const cohortsListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/cohorts",
  component: CohortsPage,
});

const cohortDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/cohorts/$cohortId",
  component: CohortDetailPage,
});

const extractionListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/extraction",
  component: ExtractionListPage,
});

const extractionEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/extraction/$configId",
  component: ExtractionEditPage,
});

const ignoreRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/ignore",
  component: IgnorePage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    indexRoute,
    runsListRoute,
    runDetailRoute,
    domainsListRoute,
    domainDetailRoute,
    checksListRoute,
    checkEditRoute,
    actionsListRoute,
    actionEditRoute,
    cohortsListRoute,
    cohortDetailRoute,
    extractionListRoute,
    extractionEditRoute,
    ignoreRoute,
  ]),
]);

const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register { router: typeof router; }
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster position="top-right" richColors theme="dark" />
    </QueryClientProvider>
  );
}
